<script lang="ts" setup>

import { computed } from '#imports';
import { useRoute } from '#vue-router';
import { useAlertsStore } from '~/stores/alerts/useAlertsStore';

import type { FieldsEventModal } from './EventModal.types';

const props = defineProps<{ data: FieldsEventModal }>();

const content = computed(() => props.data.content);

const alertStore = useAlertsStore();

const emit = defineEmits(['close-modal']);

const closeModal = () => {
    emit('close-modal');
};

const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
        alertStore.add({
            critical: false,
            description: `${text} is copied to the clipboard`,
            status: 'type.success',
            title: 'Copied'
        });
    })
        .catch((err) => {
            console.error(
                'Failed to copy text: ',
                err
            );
        });
};

const formattedAddress = (address: string | number | boolean) => {
    return typeof address === 'string' ? address.slice(0, 24) + '...' : '';
};
const route = useRoute();
const runtimeConfig = useRuntimeConfig();

const hrefShareToX = computed(() => encodeURIComponent(import.meta.env.VITE_DOMEN_URL + route.path));

const fullUrl = computed(() => {
    return window.location.origin + route.path;
});

</script>

<template>
  <Modal  :model-value="true"
          :width-content="400"
          @update:model-value="closeModal">
    <div class="width-full flex-column">

      <!-- Image -->
      <div class="width-full flex-column">
        <div class="display-flex justify-center">
          <EventModalImg v-if="props.data.images && props.data.imageState"
                         :src="props.data.images"
                         :status="props.data.imageState"
          />
        </div>

        <div class="description width-full display-flex flex-column gap-2 pb-16">
          <p v-if="props.data.title"
             class="compact-500-accent width-full text-center"> {{props.data.title}} </p>
          <template v-if="props.data.description && props.data.description.length > 0">
            <p class="display-flex gap-8 width-full justify-center color-text-grey text-400-default">
              <span>{{  props.data.description[0] }}</span>
            </p>
            <p class="display-flex gap-8 width-full justify-center color-text-grey text-400-default">
              <span class="cursor-pointer">{{  props.data.description[1] }}</span>
            </p>
          </template>
        </div>

      </div>

      <!-- Message-->
      <p class="width-full text-center display-800-accent pb-16"> {{ props.data.heading }} </p>

      <!-- Content-->
      <div v-if="Array.isArray(content) && content?.length && props.data.type !== 'cancel_offers'"
           class="pt-12">
        <p v-for="(item, index) in content"
           :key="index">
          <template v-if="typeof item !== 'object'">
            {{ item }}
          </template>
          <template v-else-if="typeof item === 'object'">
            <template v-if="item.isInput">
              <div class="display-flex flex-column gap-4 pb-16">
                <span class="compact-400-accent color-text-grey pb-8">{{ item.label }}</span>
                <div class="display-flex gap-4 align-items-center">
                  <input class="input-main compact-500-accent"
                         type="text"
                         :value="item.wallet ? formattedAddress(item.wallet) : ''"
                  >
                  <!--todo вывести нормальный тип -->
                  <IconMain class="icon-copy cursor-pointer"
                            icon="copy"
                            :size="24"
                            @click="copyToClipboard(item.wallet as any)"
                  />
                </div>
              </div>
            </template>
            <template v-else>
              <span v-for="(value, key) in item"
                    :key="key">
                <template v-if="key !== 'isBold' && key !== 'isInput' && key !== 'wallet'">
                  <strong v-if="item.isBold"
                          class="row width-full compact-500-accent display-flex justify-between">
                    <span>{{ key }}:</span>  <span>{{ value }}</span>
                  </strong>
                  <span v-else
                        class="row width-full compact-400-accent display-flex justify-between color-text-grey">
                    <span>{{ key }}</span>  <span>{{ value }}</span>
                  </span>
                </template>
              </span>
            </template>
          </template>

        </p>
      </div>

      <div v-if="Array.isArray(content) && content?.length && props.data.type == 'cancel_offers'"
           class="pb-16">
        <p v-for="(item, index) in content"
           :key="index">
          <template v-if="typeof item !== 'object'">
            {{ item }}
          </template>
          <template v-else-if="typeof item === 'object'">
            <div>
              <div class="row width-full compact-400-accent display-flex align-items-center color-text-grey">
                <div class="row-img">
                  <ImgMain v-if="'img' in item"
                           :src="item.img" />
                </div>
                <p class="width-full compact-400-accent display-flex justify-between color-text-grey"><span v-if="'title' in item">{{ item.title }}</span>  <span v-if="'price' in item">{{ item.price }}</span></p>

              </div>
            </div>
          </template>

        </p>
      </div>

      <div v-if="typeof content !== 'object'">
        <p class="width-full text-center color-text-grey text-700-numeric pb-16"> {{ props.data.content }}</p>
      </div>

      <div v-if="props.data.type == 'listed' || props.data.type == 'removed'"
           class="share-block-modal width-full display-flex flex-column gap-8">
        <p class=" width-full text-center compact-500-accent">Share to</p>
        <div class="display-flex width-full align-items-center justify-center gap-8">
          <span class="display-flex align-items-center justify-center footer-link"
                @click="copyToClipboard(fullUrl)">
            <IconMain class="cursor-pointer"
                      icon="link"
                      :size="20" />
          </span>
          <!--          TODO: нет ссылки на телеграм-->
          <!--          <a class="display-flex align-items-center justify-center footer-link"-->
          <!--             :href=""-->
          <!--             target="_blank"-->
          <!--          >-->
          <!--          <IconMain class="cursor-pointer"-->
          <!--                      icon="telegram"-->
          <!--                      :size="20" />-->
          <!--          </a>-->
          <a class="display-flex align-items-center justify-center footer-link"
             :href="`https://twitter.com/intent/tweet?text=Check%20out%20this%20item%20in%20${runtimeConfig.public.name}&url=${hrefShareToX}`"
             target="_blank"
          >
            <IconMain class="cursor-pointer"
                      icon="twitter"
                      :size="20" />
          </a>
        </div>
      </div>

      <div>
        <!-- Actions-->
        <Button v-if="props.data.button && Object.keys(props.data.button).length > 0"
                :color="'bg.primary'"
                :size="'size.medium'"
                class="popup-btn text-color-primary "
                @click="props.data.button?.callback?.()">
          <p class="width-full text-center compact-500-accent"> {{ props.data.button.title }}</p>
        </Button>

      </div>

    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.pb-16 {
  padding-bottom: 16px;
}

.pt-12 {
  padding-top: 12px;
}

.row {
  height: 40px;
}

.description {
  padding-top: 20px;
}

.share-block-modal {
  padding-top: 16px;
  padding-bottom: 32px;
}

.row-img {
  width: 28px;
  height: 28px;
  margin-right: 12px;
}

.icon-copy {
  width: 52px;
}
</style>
